import React, { useState, useMemo } from 'react';
import { adminService } from '../../../../services';
import type {
  AdminCommunicationPayload,
  CommunicationTarget,
  NotificationType,
  SentItem,
} from '../../../../types/admin';
import type { User } from '../../../../types/auth';
import {
  TYPE_CONFIG,
  TARGET_CONFIG,
  MOCK_COURSES,
  EMPTY_FORM,
} from './communicationsConfig';
import NotificationPreview from './NotificationPreview';
import SentHistory from './SentHistory';
import UserSearchSelect from './UserSearchSelect';
import './AdminCommunications.css';

type TouchedFields = { title: boolean; message: boolean; targetId: boolean };

const AdminCommunications: React.FC = () => {
  const [form, setForm] = useState<AdminCommunicationPayload>(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [history, setHistory] = useState<SentItem[]>([]);
  const [showOptional, setShowOptional] = useState(false);
  const [touched, setTouched] = useState<TouchedFields>({
    title: false,
    message: false,
    targetId: false,
  });
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  const touch = (field: keyof TouchedFields) =>
    setTouched((t) => ({ ...t, [field]: true }));

  const set = <K extends keyof AdminCommunicationPayload>(
    key: K,
    value: AdminCommunicationPayload[K]
  ) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSent(false);
    setError('');
  };

  const isValid = useMemo(
    () =>
      form.title.trim().length >= 3 &&
      form.message.trim().length >= 10 &&
      (form.target === 'all' ||
        form.target === 'unconfirmed' ||
        form.target === 'no_purchase' ||
        (form.target === 'course' &&
          (form.targetCourseId?.trim() ?? '').length > 0) ||
        (form.target === 'users' && (form.targetUserIds?.length ?? 0) > 0) ||
        (form.target === 'role' && (form.targetRole ?? '').length > 0)),
    [form]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || sending) return;
    setSending(true);
    setError('');
    const payload: AdminCommunicationPayload = {
      ...form,
      actionUrl: form.actionUrl?.trim() || undefined,
      actionLabel: form.actionLabel?.trim() || undefined,
    };
    try {
      await adminService.sendCommunication(payload);
      setHistory((h) => [
        ...h,
        { id: crypto.randomUUID(), payload, sentAt: new Date().toISOString() },
      ]);
      setSent(true);
      setForm(EMPTY_FORM);
      setSelectedUsers([]);
      setShowOptional(false);
    } catch {
      setError("Errore durante l'invio. Riprova.");
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    setForm(EMPTY_FORM);
    setSelectedUsers([]);
    setSent(false);
    setError('');
    setShowOptional(false);
    setTouched({ title: false, message: false, targetId: false });
  };

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div>
          <h1 className="ac-page-title">Comunicazioni</h1>
          <p className="ac-page-sub">
            Invia notifiche agli utenti della piattaforma
          </p>
        </div>
      </div>

      <div className="ac-layout">
        <div className="ac-form-col">
          <form onSubmit={handleSubmit} noValidate>
            {/* ── Tipo notifica ── */}
            <section className="ac-section">
              <h2 className="ac-section-title">
                <i className="bi bi-bell me-2" />
                Tipo di notifica
              </h2>
              <div className="ac-type-grid">
                {(
                  Object.entries(TYPE_CONFIG) as [
                    NotificationType,
                    (typeof TYPE_CONFIG)[NotificationType],
                  ][]
                ).map(([key, conf]) => (
                  <button
                    key={key}
                    type="button"
                    className={`ac-type-btn ${form.type === key ? 'ac-type-btn--active' : ''} ${conf.className}`}
                    onClick={() => set('type', key)}
                  >
                    <i className={`bi ${conf.icon} ac-type-btn__icon`} />
                    <span>{conf.label}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* ── Destinatari ── */}
            <section className="ac-section">
              <h2 className="ac-section-title">
                <i className="bi bi-send me-2" />
                Destinatari
              </h2>
              <div className="ac-target-grid">
                {(
                  Object.entries(TARGET_CONFIG) as [
                    CommunicationTarget,
                    (typeof TARGET_CONFIG)[CommunicationTarget],
                  ][]
                ).map(([key, conf]) => (
                  <button
                    key={key}
                    type="button"
                    className={`ac-target-btn ${form.target === key ? 'ac-target-btn--active' : ''}`}
                    onClick={() => {
                      set('target', key);
                      set('targetCourseId', '');
                      set('targetUserIds', []);
                      set('targetRole', undefined);
                    }}
                  >
                    <i className={`bi ${conf.icon} ac-target-btn__icon`} />
                    <span className="ac-target-btn__label">{conf.label}</span>
                    <span className="ac-target-btn__desc">
                      {conf.description}
                    </span>
                  </button>
                ))}
              </div>

              {form.target === 'course' && (
                <div className="ac-field mt-3">
                  <label className="ac-label" htmlFor="targetCourse">
                    Seleziona corso <span className="ac-required">*</span>
                  </label>
                  <select
                    id="targetCourse"
                    className={`ac-select ${touched.targetId && !form.targetCourseId?.trim() ? 'ac-input--error' : ''}`}
                    value={form.targetCourseId ?? ''}
                    onChange={(e) => set('targetCourseId', e.target.value)}
                    onBlur={() => touch('targetId')}
                  >
                    <option value="">— Scegli un corso —</option>
                    {MOCK_COURSES.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.title}
                      </option>
                    ))}
                  </select>
                  {touched.targetId && !form.targetCourseId?.trim() && (
                    <p className="ac-field-error">
                      <i className="bi bi-exclamation-circle me-1" />
                      Seleziona un corso
                    </p>
                  )}
                </div>
              )}

              {form.target === 'users' && (
                <div className="ac-field mt-3">
                  <label className="ac-label">
                    Utenti destinatari <span className="ac-required">*</span>
                  </label>
                  <UserSearchSelect
                    selected={selectedUsers}
                    onChange={(users) => {
                      setSelectedUsers(users);
                      set(
                        'targetUserIds',
                        users.map((u) => u.id)
                      );
                    }}
                  />
                  {touched.targetId && selectedUsers.length === 0 && (
                    <p className="ac-field-error">
                      <i className="bi bi-exclamation-circle me-1" />
                      Seleziona almeno un utente
                    </p>
                  )}
                </div>
              )}

              {form.target === 'role' && (
                <div className="ac-field mt-3">
                  <label className="ac-label" htmlFor="targetRole">
                    Ruolo <span className="ac-required">*</span>
                  </label>
                  <select
                    id="targetRole"
                    className="ac-select"
                    value={form.targetRole ?? ''}
                    onChange={(e) =>
                      set('targetRole', e.target.value as 'user' | 'admin')
                    }
                  >
                    <option value="">— Scegli un ruolo —</option>
                    <option value="user">Utenti</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              )}
            </section>

            {/* ── Contenuto ── */}
            <section className="ac-section">
              <h2 className="ac-section-title">
                <i className="bi bi-pencil me-2" />
                Contenuto
              </h2>

              <div className="ac-field">
                <label className="ac-label" htmlFor="notifTitle">
                  Titolo <span className="ac-required">*</span>
                </label>
                <input
                  id="notifTitle"
                  type="text"
                  className={`ac-input ${touched.title && form.title.trim().length < 3 ? 'ac-input--error' : ''}`}
                  placeholder="es. Nuovo contenuto disponibile"
                  value={form.title}
                  maxLength={80}
                  onChange={(e) => set('title', e.target.value)}
                  onBlur={() => touch('title')}
                />
                <div className="ac-field-footer">
                  {touched.title && form.title.trim().length < 3 && (
                    <p className="ac-field-error">
                      <i className="bi bi-exclamation-circle me-1" />
                      Minimo 3 caratteri
                    </p>
                  )}
                  <p className="ac-char-count">{form.title.length}/80</p>
                </div>
              </div>

              <div className="ac-field">
                <label className="ac-label" htmlFor="notifMessage">
                  Messaggio <span className="ac-required">*</span>
                </label>
                <textarea
                  id="notifMessage"
                  className={`ac-textarea ${touched.message && form.message.trim().length < 10 ? 'ac-input--error' : ''}`}
                  placeholder="Scrivi il testo della notifica…"
                  value={form.message}
                  maxLength={300}
                  rows={4}
                  onChange={(e) => set('message', e.target.value)}
                  onBlur={() => touch('message')}
                />
                <div className="ac-field-footer">
                  {touched.message && form.message.trim().length < 10 && (
                    <p className="ac-field-error">
                      <i className="bi bi-exclamation-circle me-1" />
                      Minimo 10 caratteri
                    </p>
                  )}
                  <p className="ac-char-count">{form.message.length}/300</p>
                </div>
              </div>

              <button
                type="button"
                className="ac-optional-toggle"
                onClick={() => setShowOptional((v) => !v)}
              >
                <i
                  className={`bi ${showOptional ? 'bi-chevron-up' : 'bi-chevron-down'} me-1`}
                />
                {showOptional ? 'Nascondi' : 'Aggiungi'} link azione (opzionale)
              </button>

              {showOptional && (
                <div className="ac-optional-fields">
                  <div className="ac-field">
                    <label className="ac-label" htmlFor="actionLabel">
                      Testo pulsante
                    </label>
                    <input
                      id="actionLabel"
                      type="text"
                      className="ac-input"
                      placeholder="es. Guarda ora"
                      value={form.actionLabel ?? ''}
                      onChange={(e) => set('actionLabel', e.target.value)}
                    />
                  </div>
                  <div className="ac-field">
                    <label className="ac-label" htmlFor="actionUrl">
                      URL destinazione
                    </label>
                    <input
                      id="actionUrl"
                      type="text"
                      className="ac-input"
                      placeholder="es. /dashboard/corsi/be-smart"
                      value={form.actionUrl ?? ''}
                      onChange={(e) => set('actionUrl', e.target.value)}
                    />
                  </div>
                </div>
              )}
            </section>

            {sent && (
              <div className="ac-feedback ac-feedback--success" role="status">
                <i className="bi bi-check-circle-fill me-2" />
                Notifica inviata con successo!
              </div>
            )}
            {error && (
              <div className="ac-feedback ac-feedback--error" role="alert">
                <i className="bi bi-exclamation-triangle me-2" />
                {error}
              </div>
            )}

            <div className="ac-form-actions">
              <button
                type="button"
                className="ac-action-btn"
                onClick={handleReset}
                disabled={sending}
              >
                <i className="bi bi-arrow-counterclockwise me-2" />
                Reimposta
              </button>
              <button
                type="submit"
                className="ac-action-btn ac-action-btn--primary"
                disabled={!isValid || sending}
              >
                {sending ? (
                  <>
                    <span className="ac-spinner me-2" />
                    Invio in corso…
                  </>
                ) : (
                  <>
                    <i className="bi bi-send me-2" />
                    Invia notifica
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        <div className="ac-preview-col">
          <NotificationPreview payload={form} />
          <SentHistory items={history} />
        </div>
      </div>
    </div>
  );
};

export default AdminCommunications;
